import axios from '@/axios';
import { $settings } from '@/provider/settings';
import { useOverviewStore } from '@/store/overview';

const resultMapper = {
  ACTIVITIES: 'activity',
  COMPANIES: 'company',
  CONTACTPERS: 'contact',
  OPPORTUNITIES: 'opportunity',
  ORDERS: 'sales.orders',
  QUOTATIONS: 'sales.quotations',
  CONTACTS: 'contact',
  EQUIPMENTS: 'equipment',
  NOTIFICATIONS: 'notification',
  DOCUMENTS: 'document',
  LEADS: 'lead',
};

const convertType = function (type, subType) {
  if (!type) return false;
  let foundType = '';

  Object.keys(resultMapper).some((searchType) => {
    const typeFound =
      resultMapper[searchType] === type || (subType && `${type}.${subType}`);
    if (typeFound) foundType = searchType.toLocaleLowerCase();

    return !!typeFound;
  });

  return foundType;
};

export default {
  fetchGlobalSearch(store, searchOptions) {
    const { param, options = {}, type, subType, count } = searchOptions;

    const { skip = 0, activeSort = {}, pageAll } = options;
    const convertedType = convertType(type, subType);
    const skipParameter = `&skip=${skip || 0}`;
    const pageSizeParameter = pageAll ? `&pageSize=999999` : '';
    const activeSortParameter =
      (activeSort.sort &&
        `&sort=${activeSort.sort} ${activeSort.asc ? 'asc' : 'desc'}`) ||
      '';

    const typeParameter = convertedType ? `?Records=${convertedType}` : '';

    return axios({
      method: 'post',
      url: `${$settings.middlewareSAPProxyURI}/global/stdsearch/${typeParameter}${skipParameter}${activeSortParameter}${pageSizeParameter}`,
      data: {
        SEARCHPARAMS: { TEXT: param },
      },
    })
      .then((response) => {
        if (response && response.data && response.data.DATA) {
          const { DATA } = response.data;

          Object.keys(DATA)
            .filter((key) => key !== 'RESULTS')
            .forEach((resultType) => {
              const [rType, rSubType] = resultMapper[resultType].split('.');
              const total =
                (DATA.RESULTS && DATA.RESULTS[`${resultType}_TOTAL`]) ||
                count ||
                0;

              const overviewObject = {
                context: pageAll ? 'globalSearchAll' : 'globalSearch',
                type: rType,
                subType: rSubType,
                payload: {
                  value: DATA[resultType],
                  total,
                },
              };

              useOverviewStore().setOverview(overviewObject);
            });
        }

        return true;
      })
      .catch((error) => {
        console.warn(`Global Search Error: ${error}`);
      });
  },
  fetchSegmentSearch(store, data) {
    const formData = new URLSearchParams();
    formData.append('$filter', data.$filter || '()');

    let records = '';

    if (data.result === 'cpOnly') {
      records = 'Records=CONTACTS,TOTALS';
    } else if (data.result === 'custOnly') {
      records = 'Records=COMPANIES,TOTALS';
    }

    return axios
      .post(
        `${$settings.middlewareSAPProxyURI}/segment/search/${data.ref}?${records}`,
        formData,
      )
      .then((response) => {
        const { DATA } = response.data;
        Object.keys(DATA)
          .filter((key) => !['RESULTS', 'TOTALS'].includes(key))
          .forEach((resultType) => {
            const [rType, rSubType] = resultMapper[resultType].split('.');
            const total = (DATA.TOTALS && DATA.TOTALS[`${resultType}`]) || 0;
            const overviewObject = {
              context: 'segmentSearch',
              type: rType,
              subType: rSubType,
              payload: {
                value: DATA[resultType],
                total,
              },
            };
            useOverviewStore().setOverview(overviewObject);
          });
      })
      .catch((error) => {
        ['company', 'contact'].forEach((type) => {
          useOverviewStore().setOverview({
            context: 'segmentSearch',
            type,
            payload: {
              value: [],
              total: 0,
            },
          });
        });

        console.warn(`Segment Search Error: ${error}`);
      });
  },
  fetchCustomSearch(store, { searchParams, type, options = {} }) {
    const { skip = 0, activeSort = {}, pageAll } = options;

    const skipParameter = `&skip=${skip || 0}`;
    const pageSizeParameter = pageAll ? `&pageSize=9999` : '';
    const activeSortParameter =
      (activeSort.sort &&
        `&sort=${activeSort.sort} ${activeSort.asc ? 'asc' : 'desc'}`) ||
      '';
    const context = pageAll ? 'customSearchAll' : 'customSearch';
    let payload = {};

    return axios({
      method: 'post',
      url: `${$settings.middlewareSAPProxyURI}/${type}/search?${searchParams}${skipParameter}${activeSortParameter}${pageSizeParameter}`,
    })
      .then((response) => {
        if (response && response.data && response.data.SUCCESS) {
          payload = {
            value: response.data.DATA,
            total: response.data.INFO.TOTAL || 0,
          };
        }

        useOverviewStore().setOverview({ context, type, payload });
      })
      .catch((error) => {
        useOverviewStore().setOverview({ context, type, payload });

        console.warn(`Custom Search Error: ${error}`);
      });
  },
  updateCustomSearch(store, { payload, type }) {
    return axios({
      method: 'put',
      url: `${$settings.middlewareSAPProxyURI}/${type}/search`,
      data: { DATA: payload },
    });
  },
  initSearch(store, { type, filter }) {
    const queryFilter = filter || '';
    return axios({
      method: 'post',
      url: `${$settings.middlewareSAPProxyURI}/${type}/search/init/?$filter=${queryFilter}`,
    })
      .then((response) => response.data && response.data.DATA)
      .catch((error) => {
        console.warn(`Init Search Error: ${error}`);
      });
  },
  resetSearchResult(store, type) {
    if (type !== 'global')
      useOverviewStore().setOverview({ context: 'customSearch', type });

    store.commit('RESET_CUSTOM_SEARCH_RESULTS', []);
  },
  fetchSearchVariants(store, type) {
    store.commit('SET_LOADING_VARIANT', true);
    axios
      .get(`${$settings.middlewareURI}/exp/${type}/searchvariant`)
      .then((result) => {
        store.commit(
          'SET_SEARCH_VARIANTS',
          Array.isArray(result.data) ? result.data : [],
        );
      })
      .catch((error) => {
        console.warn(error);
      })
      .finally(() => store.commit('SET_LOADING_VARIANT', false));
  },
  saveSearchVariant(
    store,
    { variant, content, type, forWeekPlanner, forDashboard, guid },
  ) {
    store.commit('SET_LOADING_VARIANT', true);
    const wplQuery = forWeekPlanner ? 'Wpl=X' : '';
    const dashboardQuery = forDashboard ? 'Dshb=X' : '';
    const query = [wplQuery, dashboardQuery].join('&');

    axios
      .post(`${$settings.middlewareURI}/exp/${type}/searchvariant?${query}`, {
        DATA: {
          DESCRIPTION: variant,
          CONTENT: content,
          GUID: guid,
        },
      })
      .then(({ data }) => {
        store.dispatch('fetchSearchVariants', type);
        store.commit('SET_SELECTED_SEARCH_VARIANT', {
          DESCRIPTION: variant,
          CONTENT: content,
          GUID: data.GUID,
          WPL: forWeekPlanner,
          DSHB: forDashboard,
        });
      })
      .catch((error) => {
        console.warn(error);
      })
      .finally(() => store.commit('SET_LOADING_VARIANT', false));
  },
  deleteSearchVariant(store, { guid, type }) {
    store.commit('SET_LOADING_VARIANT', true);
    axios
      .delete(`${$settings.middlewareURI}/exp/${type}/searchvariant`, {
        data: { DATA: { GUID: guid } },
      })
      .then(() => store.dispatch('fetchSearchVariants', type))
      .catch((error) => {
        console.warn(error);
      })
      .finally(() => store.commit('SET_LOADING_VARIANT', false));
  },
};
